import { hasStakeInHand } from './hasStakeInHand';
import { parseVotedSeats } from './votedSeats';

/** Mirrors MAX_RUNS in shared/config/runMulti.js — the deck budget's ceiling. */
export const MAX_RUNS = 3;

/**
 * Whose decision the multi-run vote is right now, and what they may choose.
 *
 * EN-663 AC-2.2 / AC-2.5 / AC-3.2 replaced the one-phase simultaneous vote with a
 * two-phase offer: the initiator proposes a count, then everyone else answers a
 * binary yes/no to THAT count. Three client surfaces have to agree about which
 * phase a table is in and who is being asked —
 *
 *   - the action panel (whether to render buttons at all, and which ones),
 *   - the seat's own timer ring (turn presentation: highlight + countdown),
 *   - `useActiveTables`' cross-table vote signal,
 *
 * — and the server enforces the same rule a fourth time in both writers. A seat
 * offered a button the server will refuse is the failure mode `hasStakeInHand`
 * was written for: the panel has no way to surrender a vote it already rendered
 * as cast, so the player watches a window they were never entered into expire.
 * One module, so the surfaces cannot drift apart.
 */

/**
 * The run count currently on offer. 0 = the initiator has not chosen yet.
 *
 * Segment 1 of `runs`. Mirrors `parseOffer` in the serverless voteMultiRun
 * handler and `parseMultiRunOffer` in the engine writer — the writers, which put
 * the value there. Historically this segment was "the first voter's choice"; the
 * two-phase flow makes that first voter the initiator and nobody else, so the
 * position and its meaning are unchanged and an already-loaded bundle still reads
 * it correctly.
 *
 * BOUNDED to the counts a run-out can actually be, 2..MAX_RUNS (3, shared/
 * config/runMulti.js). Anything else in that segment is a corrupt or stale row,
 * and `offeredRunCounts` below turns it straight into buttons: an offer of 1
 * rendered `[1, 1]` — RUN IT 1X twice, sharing a React key — and an offer of 7
 * a RUN IT 7X the server refuses. Out-of-range reads as "no offer yet", which
 * asks the initiator and nobody else: the same state the window opens in, so a
 * responder is never shown a choice that cannot be answered.
 */
export const parseOffer = (runs?: string): number => {
  const n = parseInt(String(runs ?? '').split('/')[1], 10);
  return Number.isInteger(n) && n >= 2 && n <= MAX_RUNS ? n : 0;
};

/**
 * Whether this seat owns the offer (AC-2.1: the first all-in of the hand).
 *
 * `game.firstAllInPlayer` is stamped WRITE-ONCE per hand by the VOLUNTARY all-in
 * sites in games/{texas,omaha}/actions.js. It is a separate column from
 * `lastAllInPlayer`, which every later all-in overwrites.
 *
 * STRICT, mirroring `mayOpenOffer` on the server: no recorded initiator means
 * NOBODY is prompted. A window cannot open without one — the processors refuse to
 * offer multi-run on a hand whose only all-in was a BLIND POST, which does not
 * count as initiating one (Sevs 2026-09-16).
 *
 * The marker is TRUSTED as-is: the processor has already checked that it names a
 * seat in the consent list before opening the window, so re-testing that seat's
 * money columns here would be a second opinion that can only disagree. And it
 * would disagree in the worst direction — those columns are absent from older
 * broadcast payloads (the deploy-skew case `hasStakeInHand` fails open for), so a
 * stake test would refuse the REAL initiator, nobody would be prompted, and the
 * window would time out to a single run with nothing to explain it. This seat's
 * own eligibility is still checked by `isSeatPrompted` below.
 */
export const isInitiatorSeat = (
  game: Record<string, unknown> | undefined,
  seatId: number
): boolean => {
  const initiator = Number((game as any)?.firstAllInPlayer);

  if (!Number.isInteger(initiator) || initiator <= 0) return false;

  return seatId === initiator;
};

/**
 * Seats the hand has already left behind: folded, or waiting for the button.
 *
 * `p{n}bet` carries a marker prefix instead of a number for both — 'F' folded,
 * 'J' joined-and-waiting — and `p{n}status` 4/5/9/10 is the other half of
 * waiting (`utils/functions::getPlayerStatus`, and `currentTable` derives
 * `isFolded`/`isWaiting` from exactly these two columns).
 *
 * Here rather than only at the callers because THREE surfaces gate on
 * `isSeatPrompted` and only two of them applied these clauses: `UserActions`
 * (index.tsx) and `useActiveTables` both spell out `isSeated && !isWaiting &&
 * !isFolded`, while the seat's own timer ring (`SeatWithPlayer`) took the
 * predicate bare. `hasStakeInHand` does not close the gap — it counts an ante
 * as stake, and a folded seat in an ante game still has one — so a seat that
 * folded before the all-in took the vote highlight and countdown on the felt
 * while the panel it was pointing at never mounted for that player. One
 * predicate, so the surfaces cannot disagree about who is being asked.
 */
const isOutOfHand = (game: Record<string, unknown> | undefined, seatId: number): boolean => {
  const marker = String((game as any)?.[`p${seatId}bet`] ?? '').substring(0, 1);

  if (marker === 'F' || marker === 'J') return true;

  return [4, 5, 9, 10].includes(Number((game as any)?.[`p${seatId}status`]));
};

/**
 * Is the multi-run window open AND waiting on THIS seat?
 *
 * Phase 1 (no offer yet): only the initiator is asked. AC-2.5 — responders are
 * never prompted until there is an offer to answer, and never at all if the
 * initiator picks 1X.
 *
 * Phase 2 (an offer stands): every eligible seat that has not answered is asked,
 * simultaneously (AC-3.1). A seat already in segment 0 has agreed and is done; a
 * seat that declined never reaches here, because a decline collapses `runs` to
 * '-2' and closes the window for the whole table (AC-4.3).
 */
export const isSeatPrompted = (
  game: Record<string, unknown> | undefined,
  seatId: number
): boolean => {
  const runs = String((game as any)?.runs ?? '');

  if (!runs.includes('v')) return false;
  if (isOutOfHand(game, seatId)) return false;
  if (!hasStakeInHand(game, seatId)) return false;

  const offer = parseOffer(runs);

  if (offer === 0) return isInitiatorSeat(game, seatId);

  return !parseVotedSeats(runs).includes(seatId);
};

/**
 * The run counts this seat may choose, in button order (AC-2.2 / AC-3.2).
 *
 * The initiator gets all three; a responder gets 1X plus the offered count and
 * nothing else — "a responder can never select a run count that wasn't offered".
 * 1 is RUN IT 1X: the initiator's "just play it once", and the responder's
 * decline. They are the same button and the same outcome, which is the point of
 * AC-5.2's one-label-one-colour rule.
 *
 * Returns [] when this seat is not being asked, so a caller that renders the
 * result cannot put a live control in front of a seat the server would refuse.
 *
 * `maxRuns` is the highest count an offer may OPEN with (EN-10921): MAX_RUNS,
 * or 2 while the ops switch withholds 3X. It narrows the initiator only. A
 * responder is always given what was actually offered, because that is what
 * the server will run, even a 3X from a client that has not caught up.
 */
export const offeredRunCounts = (
  game: Record<string, unknown> | undefined,
  seatId: number,
  maxRuns: number = MAX_RUNS
): number[] => {
  if (!isSeatPrompted(game, seatId)) return [];

  const offer = parseOffer((game as any)?.runs);

  return offer === 0 ? initiatorRunCounts(maxRuns) : [1, offer];
};

/**
 * The choices an initiator opens with: RUN IT 1X, and every multi-run count up
 * to `maxRuns`. Clamped to 2..MAX_RUNS, so a bad input can neither remove the
 * multi-run choice altogether nor offer a count the deck budget cannot deal.
 * Shared with the felt line (MultiRunVoteText), so the question on the felt
 * always matches the buttons.
 */
export const initiatorRunCounts = (maxRuns: number = MAX_RUNS): number[] => {
  const ceiling = Math.min(Math.max(Math.floor(maxRuns) || MAX_RUNS, 2), MAX_RUNS);
  const counts = [1];
  for (let runs = 2; runs <= ceiling; runs += 1) counts.push(runs);
  return counts;
};
